import React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import FilePanel from "./components/file/FilePanel";
import { IMessageBoxParams } from "../../../common/MessageBox";
import { IConfirmDialogParams } from "../../../common/ConfirmDialog";
import { getFiles, IFileRow, IProtRow } from "../../../webapi/service/getFIles";
import AuthService from "../../../common/AuthService";
import { ChartList, ProjectDetail } from "../../../common/constants";
import { IProjectRow } from "../../../webapi/service/getProjects";
import { GridSortModel } from "@mui/x-data-grid-pro";
import SimList from "./components/simulator/SimList";
import SimSettingsForm from "./components/simulator/SimSettingsForm";
import FkdppSettingsForm from "./components/fkdppmodel/FkdppSettingsForm";
import ResultsSimulationPanel from "./components/simulator/ResultsSimulationPanel";
import FkdppEvaluation from "./components/fkdppmodel/FkdppEvaluation";
import FKkdppSimulation from "./components/fkdppmodel/FKkdppSimulation";


export interface IProps extends WithTranslation {
  /**
   * 認証サービス
   */
  authService?: AuthService;
  /**
   * メインスペースの幅
   */
  mainSpaceWidth: number;
  /**
   * 選択されたプロジェクト情報
   */
  projectInfo: IProjectRow;
  /**
   * 初期表示する画面。
   * constants.tsxのProjectDetail参照
   */
  mode: number | undefined;
  /**
   * ローディング中の場合はtrue
   */
  isLoading: boolean;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
  /**
   * メッセージBoxを表示します
   * @param params - メッセージBoxパラメータ
   */
  showMessage: (params: IMessageBoxParams) => void;
  /**
   * 確認ダイアログを表示します。
   * @param params - 確認ダイアログパラメータ
   */
  showConfirm: (params: IConfirmDialogParams) => void;
  /**
   * 通信エラーが発生した場合に呼び出します
   * @param id - メッセージID
   * @param status - httpステータスコード
   */
  onCommunicationError: (
    id: string | undefined,
    status: number | undefined
  ) => void;
  /**
   * 前画面に戻る場合に呼び出します
   */
  onBack: () => void;
  /**
   * 非操作タイムアウトをリセットする場合に呼び出します
   */
  resetUnoperatedTimeout: () => void;
}

interface IState {
  /**
   * 表示する画面。
   * constants.tsxのProjectDetail参照
   */
  dispMode: number | undefined;
  /**
   * ファイル情報一覧
   */
  rows: IFileRow[];
  /**
   * チャート描画情報一覧
   */
  prots: IProtRow[];
  /**
   * チャート描画順
   */
  sortIds: number[];
  /**
   * ファイル一覧のソート条件
   */
  sortModel: GridSortModel;
}

class ProjectDetailMainSpace extends React.Component<IProps, IState> {
  private readonly ChartListThreshold = ChartList.CHART_LIST_THRESHOLD;

  constructor(props: IProps) {
    super(props);

    this.state = {
      dispMode: undefined,
      rows: [],
      prots: [],
      sortIds: [],
      sortModel: [],
    };

    this.handleSetDispMode = this.handleSetDispMode.bind(this);
    this.handleLoadFiles = this.handleLoadFiles.bind(this);
    this.handleSetSortModel = this.handleSetSortModel.bind(this);
    this.handleSetSortIds = this.handleSetSortIds.bind(this);
  }

  handleLoadFiles(loading: boolean, mode?: number) {
    const { authService, projectInfo, onLoading, onCommunicationError } =
      this.props;
    const { projectid } = projectInfo;
    const dispMode = mode ? mode : ProjectDetail.FILES;
    const prevDispMode = this.state.dispMode;
    onLoading(loading);
    getFiles({
      authService: authService,
      projectid: projectid,
      threshold: this.ChartListThreshold,
      success: (rows, prots) => {
        this.setState({
          dispMode: dispMode,
          rows: rows,
          prots: prots,
          sortIds: prots.map((prot) => {
            return prot.id;
          }),
          sortModel: [],
        });
        switch (dispMode) {
          case ProjectDetail.EVALUATE_MODEL:
          case ProjectDetail.SETTINGS_FKDPPMODEL:
          case ProjectDetail.SIMULATE_MODEL:
          case ProjectDetail.LIST_SIMULATOR:
          case ProjectDetail.RESULT_SIMULATION_SIMULATOR:
            if (prevDispMode === dispMode) {
              onLoading(false);
            }
            break;
          default:
            onLoading(false);
            break;
        }
      },
      fail: (id, status) => {
        onCommunicationError(id, status);
      },
    });
  }

  componentDidMount() {
    this.handleLoadFiles(true, this.props.mode);
  }

  componentDidUpdate(prevProps: IProps) {
    if (
      this.props.projectInfo.projectid !== prevProps.projectInfo.projectid ||
      this.props.mode !== prevProps.mode
    ) {
      this.handleLoadFiles(true, this.props.mode);
    }
  }

  handleSetDispMode(mode: number) {
    if (this.state.dispMode !== mode) {
      this.props.resetUnoperatedTimeout();
      this.setState({ dispMode: mode });
    }
  }

  handleSetSortModel(sortModel: GridSortModel) {
    this.setState({ sortModel: sortModel });
  }

  handleSetSortIds(sortIds: number[]) {
    this.setState({ sortIds: sortIds });
  }

  /**
   * プロジェクト詳細画面内メニューボタンの設定
   * ケースごとに遷移先が決まる
   * contents内のパラメータはIPropsインターフェースのものと一致
   */
  getContents() {
    const {
      authService,
      mainSpaceWidth,
      projectInfo,
      onBack,
      onLoading,
      showMessage,
      showConfirm,
      onCommunicationError,
      resetUnoperatedTimeout,
    } = this.props;
    const { 
      dispMode, 
      rows, 
      prots,  
      sortIds, 
      sortModel 
    } = this.state;

    let contents = <div />;
    switch (dispMode) {
      case ProjectDetail.FILES:
        contents = (
          <FilePanel
            authService={authService}
            mainSpaceWidth={mainSpaceWidth}
            projectInfo={projectInfo}
            setDispMode={this.handleSetDispMode}
            rows={rows}
            prots={prots}
            sortIds={sortIds}
            sortModel={sortModel}
            onSetSortModel={this.handleSetSortModel}
            onSetSortIds={this.handleSetSortIds}
            onLoadFiles={this.handleLoadFiles}
            onBack={onBack}
            onLoading={onLoading}
            showMessage={showMessage}
            showConfirm={showConfirm}
            onCommunicationError={onCommunicationError}
            resetUnoperatedTimeout={resetUnoperatedTimeout}
          />
        );
        break;
      case ProjectDetail.CREATE_SIMULATOR:
        contents = (
          <div>
            <SimSettingsForm
              authService={authService}
              projectInfo={projectInfo}
              rows={rows}
              onBack={() => this.handleSetDispMode(ProjectDetail.FILES)}
              onTop={onBack}
              onLoading={onLoading}
              showMessage={showMessage}
              showConfirm={showConfirm}
              onCommunicationError={onCommunicationError}
              resetUnoperatedTimeout={resetUnoperatedTimeout}
            />
          </div>
        )
        break;
      case ProjectDetail.LIST_SIMULATOR:
        contents = (
          <div>
            <SimList
              authService={authService}
              projectInfo={projectInfo}
              onLoadFiles={this.handleLoadFiles}
              onBack={() => this.handleSetDispMode(ProjectDetail.FILES)}
              onTop={onBack}
              onLoading={onLoading}
              showMessage={showMessage}
              showConfirm={showConfirm}
              onCommunicationError={onCommunicationError}
              resetUnoperatedTimeout={resetUnoperatedTimeout}
            />
          </div>
        )
        break;
      case ProjectDetail.RESULT_SIMULATION_SIMULATOR:
        contents = (
          <div>
            <ResultsSimulationPanel
              authService={authService}
              mainSpaceWidth={mainSpaceWidth}
              projectInfo={projectInfo}
              rows={rows}
              onBack={() => this.handleSetDispMode(ProjectDetail.FILES)}
              onLoadFiles={this.handleLoadFiles}
              onLoading={onLoading}
              showMessage={showMessage}
              showConfirm={showConfirm}
              onCommunicationError={onCommunicationError}
              resetUnoperatedTimeout={resetUnoperatedTimeout}
            />
          </div>
        )
        break;
      case ProjectDetail.SETTINGS_FKDPPMODEL:
        contents = (
          <div>
            <FkdppSettingsForm
              authService={authService}
              projectInfo={projectInfo}
              rows={rows}
              onLoadFiles={this.handleLoadFiles}
              onBack={() => this.handleSetDispMode(ProjectDetail.FILES)}
              onTop={onBack}
              onLoading={onLoading}
              showMessage={showMessage}
              showConfirm={showConfirm}
              onCommunicationError={onCommunicationError}
              resetUnoperatedTimeout={resetUnoperatedTimeout}
            />
          </div>
        )
        break;      
      case ProjectDetail.EVALUATE_MODEL:
        contents = (
          <div>
            <FkdppEvaluation 
              authService={authService}
              projectInfo={projectInfo}
              onBack={() => this.handleSetDispMode(ProjectDetail.FILES)}
              onTop={onBack}
              onLoadFiles={this.handleLoadFiles}
              onLoading={onLoading}
              showMessage={showMessage}
              showConfirm={showConfirm}
              onCommunicationError={onCommunicationError}
              resetUnoperatedTimeout={resetUnoperatedTimeout}
            />
          </div>
        )
        break;
      case ProjectDetail.SIMULATE_MODEL:
        contents = (
          <div>
            <FKkdppSimulation 
              authService={authService}
              projectInfo={projectInfo}
              onBack={() => this.handleSetDispMode(ProjectDetail.FILES)}
              onTop={onBack}
              onLoadFiles={this.handleLoadFiles}
              onLoading={onLoading}
              showMessage={showMessage}
              showConfirm={showConfirm}
              onCommunicationError={onCommunicationError}
              resetUnoperatedTimeout={resetUnoperatedTimeout}
            />
          </div>
        )
    }
    return contents;
  }

  render() {
    const contents = this.getContents();
    return contents;
  }
}

export default withTranslation()(ProjectDetailMainSpace);
